// src/components/network/messaging/ConversationView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, limit, 
  getDocs, doc, updateDoc, addDoc, serverTimestamp,
  getDoc, increment, onSnapshot
} from 'firebase/firestore';
import { Building, User, Send, ArrowLeft } from 'lucide-react';

const ConversationView = ({ conversation, onBack }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Real-time messages subscription
  useEffect(() => {
    if (!conversation?.id || !currentUser) return;
    
    // Create messages query
    const messagesQuery = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversation.id),
      orderBy('createdAt', 'asc'),
      limit(100)
    );
    
    // Subscribe to messages
    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        setMessages(messagesData);
        setLoading(false);
        
        // Mark conversation as read
        markConversationAsRead();
      },
      (error) => {
        console.error('Error fetching messages:', error);
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [conversation, currentUser]);
  
  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Mark conversation as read
  const markConversationAsRead = async () => {
    if (!conversation?.id || !currentUser || !conversation.unreadCount) return;
    
    try {
      // Only update if there are unread messages
      if (conversation.unreadCount > 0) {
        const conversationRef = doc(db, 'conversations', conversation.id);
        
        // Create an update object to set this user's unread count to 0
        const updateData = {
          [`unreadCount.${currentUser.uid}`]: 0
        };
        
        await updateDoc(conversationRef, updateData);
      }
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  };
  
  // Send a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() || !currentUser || !conversation?.id) return;
    
    try {
      setSending(true);
      
      // Create new message
      const newMessage = {
        conversationId: conversation.id,
        senderId: currentUser.uid,
        text: messageText.trim(),
        createdAt: serverTimestamp(),
        read: false
      };
      
      // Add to messages collection
      await addDoc(collection(db, 'messages'), newMessage);
      
      // Update conversation with last message info
      const conversationRef = doc(db, 'conversations', conversation.id);
      
      // Create update object
      const updateData = {
        lastMessage: messageText.trim(),
        lastMessageAt: serverTimestamp(),
        lastMessageSenderId: currentUser.uid
      };
      
      // Increment unread count for the other user
      const otherUserId = conversation.participant.id;
      updateData[`unreadCount.${otherUserId}`] = increment(1);
      
      await updateDoc(conversationRef, updateData);
      
      // Clear input
      setMessageText('');
      
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };
  
  // Format date
  const formatMessageTime = (date) => {
    if (!date) return '';
    
    return new Date(date).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    
    messages.forEach(message => {
      const date = new Date(message.createdAt).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    
    return Object.entries(groups).map(([date, messages]) => ({
      date,
      messages
    }));
  };
  
  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 p-8 text-center">
        <div>
          <Building size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600 mb-2">Select a conversation</p>
          <p className="text-gray-500 text-sm">
            Choose a conversation from the list or start a new one
          </p>
        </div>
      </div>
    );
  }
  
  const { participant } = conversation;
  const messageGroups = groupMessagesByDate(messages);
  
  return (
    <div className="flex flex-col h-full">
      {/* Conversation header */}
      <div className="p-3 border-b border-gray-200 flex items-center">
        <button 
          onClick={onBack}
          className="p-1 mr-2 text-gray-500 hover:text-gray-700 md:hidden"
        >
          <ArrowLeft size={20} />
        </button>
        
        <div className="w-10 h-10 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
          {participant.type === 'business' && participant.logoUrl ? (
            <img 
              src={participant.logoUrl} 
              alt={participant.name} 
              className="w-full h-full object-cover"
            />
          ) : participant.type === 'business' ? (
            <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
              <Building size={18} />
            </div>
          ) : participant.photoURL ? (
            <img 
              src={participant.photoURL} 
              alt={participant.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
              <User size={18} />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{participant.name}</h3>
          {participant.type === 'business' && participant.industry && (
            <p className="text-xs text-gray-500 truncate">{participant.industry}</p>
          )}
        </div>
      </div>
      
      {/* Messages container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : ''}`}>
                <div className={`animate-pulse max-w-[80%] p-3 rounded-lg ${
                  i % 2 === 0 ? 'bg-gray-200' : 'bg-purple-100'
                }`}>
                  <div className="h-3 bg-gray-300 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-40"></div>
                </div>
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No messages yet</p>
            <p className="text-sm text-gray-400 mt-1">Start the conversation!</p>
          </div>
        ) : (
          messageGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="space-y-4">
              {/* Date separator */}
              <div className="flex justify-center">
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                  {new Date(group.date).toLocaleDateString(undefined, { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              
              {/* Messages */}
              {group.messages.map((message) => {
                const isCurrentUser = message.senderId === currentUser?.uid;
                
                return (
                  <div key={message.id} className={`flex ${isCurrentUser ? 'justify-end' : ''}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      isCurrentUser 
                        ? 'bg-[#190192] text-white rounded-tr-none' 
                        : 'bg-gray-100 text-gray-800 rounded-tl-none'
                    }`}>
                      <p className="mb-1">{message.text}</p>
                      <p className={`text-xs text-right ${
                        isCurrentUser ? 'text-purple-200' : 'text-gray-500'
                      }`}>
                        {formatMessageTime(message.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message input */}
      <div className="p-3 border-t border-gray-200">
        <form onSubmit={handleSendMessage} className="flex">
          <input
            type="text"
            className="flex-1 border border-gray-200 rounded-l-lg px-3 py-2 focus:outline-none focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Type a message..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !messageText.trim()}
            className="px-4 py-2 bg-[#190192] text-white rounded-r-lg hover:bg-[#2C0DB5] disabled:bg-gray-400"
          >
            {sending ? (
              <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConversationView;